import { useState, useCallback, useMemo } from 'react';
import { Calendar } from '@parrot-software/design-system';
import { DateRangePresets } from '@reports/domain/types';
import { createReportDateRange, createReportDateRangeFromPreset } from '@reports/domain/value-objects';
import type { ReportDateRange } from '@reports/domain/value-objects';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

interface ReportCalendarProps {
  value: ReportDateRange;
  onChange: (dateRange: ReportDateRange) => void;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * ReportCalendar - Calendar component for date range selection
 *
 * Handles all calendar-specific behavior including:
 * - Internal calendar state management
 * - Date range selection logic
 * - Custom formatters for Spanish locale
 * - Disabling future dates
 * - Smart month navigation: shows selected start date's month for DATE_RANGE preset,
 *   or current month for other presets (TODAY, YESTERDAY, etc.)
 *
 * @param {ReportCalendarProps} props - Component properties
 * @param {ReportDateRange} props.value - Current date range value
 * @param {(dateRange: ReportDateRange) => void} props.onChange - Callback when date range selection is complete
 * @param {boolean} props.isOpen - Whether the calendar popover is open
 * @param {(open: boolean) => void} props.onOpenChange - Callback when open state changes
 * @returns {React.ReactElement} Calendar component wrapped in PopoverContent
 *
 * @example
 * <ReportCalendar
 *   value={dateRange}
 *   onChange={handleDateRangeChange}
 *   isOpen={isCalendarOpen}
 *   onOpenChange={setIsCalendarOpen}
 * />
 */
export const ReportCalendar = ({ value, onChange, isOpen, onOpenChange }: ReportCalendarProps) => {
  const isRangePreset = value.preset === DateRangePresets.DATE_RANGE;
  const initialCalendarValue = isRangePreset ? value : createReportDateRangeFromPreset(DateRangePresets.TODAY);
  const monthToDisplay = isRangePreset ? value.start : new Date();

  const [calendarValue, setCalendarValue] = useState<Partial<ReportDateRange>>(initialCalendarValue);
  const [displayedMonth, setDisplayedMonth] = useState<Date>(monthToDisplay);

  // Handle calendar range selection
  const handleCalendarRangeSelect = useCallback(
    (range: { from?: Date; to?: Date } | undefined) => {
      if (!range?.from) return;

      setCalendarValue((prev) => {
        if (prev.start && prev.end) {
          const isSameStart = prev.start.getTime() === range.from?.getTime();

          return {
            preset: DateRangePresets.DATE_RANGE,
            start: isSameStart ? range.to : range.from,
            end: undefined,
          };
        }

        const newDateRange = createReportDateRange(range.from as Date, range.to as Date, DateRangePresets.DATE_RANGE);
        onChange(newDateRange);
        onOpenChange(false);

        return newDateRange;
      });
    },
    [onChange, onOpenChange],
  );

  // Get calendar selected dates
  const selectedCalendarValue = useMemo(() => {
    return { from: calendarValue.start, to: calendarValue.end };
  }, [calendarValue]);

  // Custom formatters to capitalize month names
  const calendarFormatters = useMemo(
    () => ({
      formatMonth: (date: Date) => {
        const monthName = format(date, 'MMMM yyyy', { locale: es });
        return monthName.charAt(0).toUpperCase() + monthName.slice(1);
      },
      formatMonthCaption: (date: Date) => {
        const monthName = format(date, 'MMMM yyyy', { locale: es });
        return monthName.charAt(0).toUpperCase() + monthName.slice(1);
      },
    }),
    [],
  );

  return (
    <Calendar
      disabled={(date: Date) => date > new Date()}
      formatters={calendarFormatters}
      mode="range"
      month={displayedMonth}
      onMonthChange={setDisplayedMonth}
      onSelect={handleCalendarRangeSelect}
      selected={selectedCalendarValue}
    />
  );
};
